import { updateTutorialState } from '@webapps/business';
import { useAuth, useMutation, useToast, useQuery, Loader } from '@webapps/reactjs-tech-components';
import { getInfosDashboard } from '@webapps/business';
import { useStyles } from '@webapps/styles';
import { createElement, useEffect, useState } from 'react';
import Step from './Components/Step';
import Modal from 'components/Modal';
import { set } from 'date-fns';

const Tutorial = ({showTutorial, setShowTutorial}) => {

  const {
    classes,
    global,
    cx,
    theme: { spacing },
  } = useStyles(styles);

  const [currentStep, setCurrentStep] = useState(0);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [steps, setSteps] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [contents, setContents] = useState([]);
  const [emergencyClose, setEmergencyClose] = useState(false);
  const { idUser, profile, isPremium } = useAuth();
  const { addToast } = useToast();
  const { NX_PROXY_API } = process.env;

  const { data: dashboardInfo = {}, refetch } = useQuery(getInfosDashboard, {
    key: 'infoDashboardTutorial',
    variables: { id: idUser },
  });

  const {
    isSuccess,
    mutateAsync: mutateUpdateTutorialState,
  } = useMutation(updateTutorialState);

  const handleTutorialOnClose = () => {
    mutateUpdateTutorialState({ id: idUser, state: true });
  };

  const handleEmergencyClose = () => {
    setEmergencyClose(true);
    setContents([]);
    setCurrentStep(0);
  }

  const handleNextStep = () => {
    setCurrentStep((prev) => prev + 1);
  };

  const handlePrevStep = () => {
    setCurrentStep((prev) => prev - 1);
  };

  const getAnnotations = (text) => {
    const classNames = [];

    text?.annotations?.bold && classNames.push(classes.bold);
    text?.annotations?.italic && classNames.push(classes.italic);
    text?.annotations?.strikethrough && classNames.push(classes.strikethrough);
    text?.annotations?.underline && classNames.push(classes.underline);

    return classNames;
  };

  const getFinalText = (text) => {
    let finalText = text.map((text, index) => {
      const classNames = getAnnotations(text);
      return (
        <span key={index} className={classNames.length > 0 ? classNames.join(' ') : undefined}>
          {text?.href ? <a href={text.href}>{text.plain_text}</a> : text.plain_text}
        </span>
      );
    });
    return finalText;
  };

  const filterElements = (data) => {
    
    const elements = [];

    data.results && data.results.forEach((element) => {

      const typeMappings = {
        paragraph: { tag: 'p', className: classes.text },
        heading_1: { tag: 'h1', className: classes.h1 },
        heading_2: { tag: 'h2', className: classes.h2 },
        heading_3: { tag: 'h3', className: classes.h3 },
        heading_4: { tag: 'h4', className: classes.h4 },
        heading_5: { tag: 'h5', className: classes.h5 },
        heading_6: { tag: 'h6', className: classes.h6 },
      };
      const type = element.type;

      if (type === "unsupported") return;
      
      if (element[type]?.rich_text && typeMappings[type]) {
        const { tag, className } = typeMappings[type];
        const finalText = getFinalText(element[type].rich_text);
        elements.push(createElement(tag, { className }, finalText));
      }

      if (type === "image") {
        elements.push(<img className={classes.image} src={element[type].file.url} alt="image" />);
      }
      if (type === "bulleted_list_item") {
        elements.push(<li className={classes.bulletList}>{element[type].rich_text[0].plain_text}</li>);
      }
      if (type === "numbered_list_item") {
        elements.push(<li className={classes.bulletList}>{element[type].rich_text[0].plain_text}</li>);
      }
      if (type === "divider") {
        elements.push(<hr />);
      }
      if (type === "callout") {
        elements.push(<blockquote>{element[type].rich_text[0].plain_text}</blockquote>);
      }
    });

    return elements;
  }

  async function getNotionData(blockId) {
    let response = "";
    if(windowWidth > 650){
      if (profile === "BUSINESS" && !isPremium) {
        response = await fetch(`${NX_PROXY_API}/notion/tutorial/business/${blockId}`);
      } else if (profile === "SCHOOL" && !isPremium) {
        response = await fetch(`${NX_PROXY_API}/notion/tutorial/school/${blockId}`);
      } else if (profile === "INDIVIDUAL") {
        response = await fetch(`${NX_PROXY_API}/notion/tutorial/student/${blockId}`);
      } else if (profile === "BUSINESS" && isPremium) {
        response = await fetch(`${NX_PROXY_API}/notion/tutorial/businessPremium/${blockId}`);
      } else if (profile === "SCHOOL" && isPremium) {
        response = await fetch(`${NX_PROXY_API}/notion/tutorial/schoolPremium/${blockId}`);
      } else if (profile === "CFA") {
        response = await fetch(`${NX_PROXY_API}/notion/tutorial/cfa/${blockId}`);
      }
    } else {
      if (profile === "BUSINESS" && !isPremium) {
        response = await fetch(`${NX_PROXY_API}/notion/tutorial/businessMobile/${blockId}`);
      } else if (profile === "SCHOOL" && !isPremium) {
        response = await fetch(`${NX_PROXY_API}/notion/tutorial/schoolMobile/${blockId}`);
      } else if (profile === "INDIVIDUAL") {
        response = await fetch(`${NX_PROXY_API}/notion/tutorial/studentMobile/${blockId}`);
      } else if (profile === "BUSINESS" && isPremium) {
        response = await fetch(`${NX_PROXY_API}/notion/tutorial/businessPremiumMobile/${blockId}`);
      } else if (profile === "SCHOOL" && isPremium) {
        response = await fetch(`${NX_PROXY_API}/notion/tutorial/schoolPremiumMobile/${blockId}`);
      } else if (profile === "CFA") {
        response = await fetch(`${NX_PROXY_API}/notion/tutorial/cfaMobile/${blockId}`);
      }
    }
  
    const data = await response.json();
    return filterElements(data);
  }


  async function getNotionPages(){

    let response = ""

    if(isFetching) return;
    setIsFetching(true);

    try{
      if(windowWidth > 650){
        if(profile === "BUSINESS" && !isPremium){
          response = await fetch(`${NX_PROXY_API}/notion/tutorial/business/ff61a58718a14890823e3120a4f77da0`);
        } else if(profile === "SCHOOL" && !isPremium){
          response = await fetch(`${NX_PROXY_API}/notion/tutorial/school/9b32881c188e4ddc9646326a682e81fb`);
        } else if(profile === "INDIVIDUAL"){
          response = await fetch(`${NX_PROXY_API}/notion/tutorial/student/3cde625931aa439b9e342f52ceab321e`);
        } else if(profile === "BUSINESS" && isPremium){
          response = await fetch(`${NX_PROXY_API}/notion/tutorial/businessPremium/01461d49765d4571bda5f2d08e1d347d`);
        } else if(profile === "SCHOOL" && isPremium){
          response = await fetch(`${NX_PROXY_API}/notion/tutorial/schoolPremium/bdf355e8c0554f6c87bb4815c9984242`);
        } else if (profile === "CFA") {
          response = await fetch(`${NX_PROXY_API}/notion/tutorial/cfa/todefine`);
        }
      } else {
        if(profile === "BUSINESS" && !isPremium){
          response = await fetch(`${NX_PROXY_API}/notion/tutorial/businessMobile/85088bea59024a2c8e6c20bb0acb6e99`);
        } else if(profile === "SCHOOL" && !isPremium){
          response = await fetch(`${NX_PROXY_API}/notion/tutorial/schoolMobile/3915bf37221c45ccb432551edb68c1f7`);
        } else if(profile === "INDIVIDUAL"){
          response = await fetch(`${NX_PROXY_API}/notion/tutorial/student/32aae51b92a74bc38d03cae1e8f3ba16`);
        } else if(profile === "BUSINESS" && isPremium){
          response = await fetch(`${NX_PROXY_API}/notion/tutorial/businessPremiumMobile/d3f9b06196324b78a228d558c8292332`);
        } else if(profile === "SCHOOL" && isPremium){
          response = await fetch(`${NX_PROXY_API}/notion/tutorial/schoolPremiumMobile/877ca50608da47c9a214b4632c25735e`);
        } else if (profile === "CFA") {
          response = await fetch(`${NX_PROXY_API}/notion/tutorialCFAMobile/todefine`);
        }
      }
    } catch (error) {
      handleEmergencyClose()
    }

    const data = await response.json();

    try {
      if (data?.results) {
        const elementsPromises = data.results.map(async (element, i) => {
    
          if (element.type === "toggle") {
            return await getNotionData(element.id);
          }
    
          return null; // or handle other cases if needed
        });
    
        // Wait for all promises to resolve
        const resolvedElements = await Promise.all(elementsPromises);
    
        // Filter out null elements and update the state
        setContents((prevLoadingContent) => [
          ...prevLoadingContent,
          ...resolvedElements.filter((el) => el !== null),
        ]);
        setIsFetching(false);
      } else {
        handleEmergencyClose()
      }
    } catch (error) {
      handleEmergencyClose()
      console.log(error)
    }
  }

  useEffect(() => {
    if(showTutorial){
      refetch();
      setShowTutorial(false);
    }
  }, [showTutorial])

  useEffect(() => {
    if(emergencyClose){
      handleTutorialOnClose()
      addToast({
        status: 'error',
        autoDelete: false,
        message: 'Erreur lors du chargement du tutoriel.',
      });
      setEmergencyClose(false)
    }
  }, [emergencyClose])

  useEffect(() => {
    if(dashboardInfo?.profile?.tutorial === false && !contents.length && isPremium !== null){
      getNotionPages()
    }
  }, [dashboardInfo, isPremium, profile])

  useEffect(() => {
    if(isSuccess){
      refetch();
      addToast({
        status: 'success',
        message: 'Pour rejouer le tutoriel, rendez-vous sur "Besoin d\'aide".',
      });
      setTimeout(() => {
        setCurrentStep(0);
      } , 1000)
    }
}, [isSuccess]);

  useEffect(() => {
    if(contents?.length > 0) {
      setSteps(
        contents.map((content, index) => (
          <Step
            step={index + 1}
            totalSteps={contents.length}
            content={content}
            next={handleNextStep}
            prev={handlePrevStep}
            onClose={handleTutorialOnClose}
            handleEmergencyClose={handleEmergencyClose}
          />
        ))
      )
    }
  } , [contents]);

  return dashboardInfo?.profile ? (
    <Modal
      open={!dashboardInfo?.profile?.tutorial}
      width={800}
      closable={false}
    >
      <div className={classes.container}>{steps[currentStep] || 'Chargement des étapes... '}</div>
    </Modal>
  ) : (<div></div>);
};

const styles = ({ fontSizes, spacing }) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    margin: spacing(1),
  },
  image: {
    width: "100%",
    height: '354px',
  },
  h2: {
    fontSize: "24px",
    fontWeight: 'bold',
    textAlign: 'center',
    margin: spacing(2, 0, 0, 0),
    color: '#152652',
  },
  text: {
    fontSize: "18px",
    textAlign: 'center',
    color: '#152652',
  },
  step: {
    fontSize: "18px",
    textAlign: 'center',
    margin: spacing(2),
    color: '#152652',
  },
  bold: {
    fontWeight: 700,
  },
  italic: {
    fontStyle: 'italic',
  },
  strikethrough: {
    textDecoration: 'line-through',
  },
  underline: {
    textDecoration: 'underline',
  },
});

export default Tutorial;